/**
 * Factor tree. Tap the number and the smallest prime falls out of it; keep
 * tapping until only a prime is left. A young child splits 12 into 2, 2, 3; an
 * older one takes 2431 apart into 11 x 13 x 17 or finds that 8191 will not
 * split at all.
 */

import { useEffect, useRef, useState } from 'preact/hooks';
import type { ComponentChildren, JSX } from 'preact';
import { Home, RotateCcw, Undo2, Wand2 } from 'lucide-preact';
import type { FactorTree } from '../../lib/factor.ts';
import {
  MAX_FACTOR_TARGET,
  canSplit,
  createTree,
  factorHint,
  factorString,
  isTreeComplete,
  powerString,
  splitAll,
  splitOnce,
  treeRemainder,
} from '../../lib/factor.ts';
import type { Spark } from '../../engine/render.ts';
import { drawSparks, spawnSparks, updateSparks } from '../../engine/render.ts';
import { partyColors, token } from '../../engine/palette.ts';
import { play } from '../../engine/audio.ts';
import { useSurface } from '../../hooks/useSurface.ts';
import { Chip, Coach, ToyButton } from '../../ui/controls.tsx';
import { NumberPad } from '../../ui/NumberPad.tsx';

const TOP_RESERVE = 118;
const BOTTOM_RESERVE = 92;
const FONT = 'Fredoka, ui-rounded, system-ui, sans-serif';

interface Props {
  onExit: () => void;
  tabs: ComponentChildren;
}

interface NodeBox {
  x: number;
  y: number;
  r: number;
  value: number;
  prime: boolean;
}

export function FactorToy({ onExit, tabs }: Props): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sparksRef = useRef<Spark[]>([]);
  const nodesRef = useRef<NodeBox[]>([]);
  const clockRef = useRef(0);

  const [tree, setTree] = useState<FactorTree>(() => createTree(360));
  const [editing, setEditing] = useState(false);

  const live = useRef(tree);
  live.current = tree;
  const complete = isTreeComplete(tree);

  const wasComplete = useRef(complete);
  useEffect(() => {
    if (complete && !wasComplete.current && tree.taken.length > 0) {
      play('yay', 0.8);
      const rect = canvasRef.current?.getBoundingClientRect();
      if (rect) spawnSparks(sparksRef.current, { x: rect.width / 2, y: rect.height * 0.5 }, partyColors(), 24, 380);
    }
    wasComplete.current = complete;
  }, [complete, tree.taken.length]);

  const split = () => {
    const current = live.current;
    if (!canSplit(current)) {
      play('thud', 0.4);
      return;
    }
    play('count', 0.6);
    setTree(splitOnce(current));
  };

  useSurface(canvasRef, {
    onFrame: (ctx, dt, { width, height }) => {
      clockRef.current += dt;
      updateSparks(sparksRef.current, dt);
      const t = live.current;

      ctx.fillStyle = token('--paper');
      ctx.fillRect(0, 0, width, height);

      // The trunk runs down the middle-left; each prime hangs off to the right.
      const levels = t.taken.length + 1;
      const usableH = height - TOP_RESERVE - BOTTOM_RESERVE;
      const step = Math.min(96, usableH / Math.max(1, levels - 0.2));
      // The floor has to allow for the deepest tree a seven-digit number can
      // make — twenty-three rungs for a power of two — without them colliding.
      const radius = Math.max(12, Math.min(34, step * 0.36));
      const trunkX = width * 0.34;
      const branchX = width * 0.72;

      const nodes: NodeBox[] = [];
      let value = t.target;
      for (let i = 0; i < levels; i++) {
        const y = TOP_RESERVE + step * (i + 0.4);
        const isLast = i === levels - 1;
        nodes.push({ x: trunkX, y, r: radius * (i === 0 ? 1.18 : 1), value, prime: isLast && !canSplit(t) });
        if (!isLast) {
          const prime = t.taken[i];
          nodes.push({ x: branchX, y: y + step * 0.55, r: radius * 0.86, value: prime, prime: true });
          value = value / prime;
        }
      }
      nodesRef.current = nodes;

      // Branches first so the circles sit on top of them.
      ctx.strokeStyle = token('--ink-faint');
      ctx.lineWidth = 3;
      for (let i = 0; i + 2 < nodes.length; i += 2) {
        const parent = nodes[i];
        const prime = nodes[i + 1];
        const child = nodes[i + 2];
        line(ctx, parent, prime);
        line(ctx, parent, child);
      }

      for (const node of nodes) {
        const pulse = node.prime ? 0 : Math.sin(clockRef.current * 2.4) * 0.03;
        ctx.beginPath();
        ctx.arc(node.x, node.y, node.r * (1 + pulse), 0, Math.PI * 2);
        ctx.fillStyle = node.prime ? token('--crayon-green') : token('--panel');
        ctx.fill();
        ctx.lineWidth = 3.5;
        ctx.strokeStyle = token('--ink');
        ctx.stroke();

        ctx.fillStyle = node.prime ? token('--panel') : token('--ink');
        ctx.font = `700 ${fitFont(String(node.value), node.r)}px ${FONT}`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(String(node.value), node.x, node.y);
      }

      drawSparks(ctx, sparksRef.current);
    },
    onDown: (p) => {
      // Tapping the number that is still splittable is the whole interaction.
      const hit = nodesRef.current.find((n) => !n.prime && Math.hypot(n.x - p.pos.x, n.y - p.pos.y) <= n.r + 14);
      if (hit) split();
    },
  });

  return (
    <div class="screen">
      <div class="stage">
        <canvas ref={canvasRef} aria-label="Factor tree" />
        <div class="topbar">
          <ToyButton round label="Home" hideLabel onClick={onExit}>
            <Home size={24} strokeWidth={2.5} />
          </ToyButton>
          <div class="topbar__group">
            <ToyButton
              round
              label="Split it all the way"
              hideLabel
              disabled={complete}
              onClick={() => {
                play('sparkle', 0.7);
                setTree(splitAll(live.current));
              }}
            >
              <Wand2 size={24} strokeWidth={2.5} />
            </ToyButton>
            <ToyButton
              round
              label="Undo"
              hideLabel
              disabled={tree.taken.length === 0}
              onClick={() => {
                play('pop', 0.4);
                setTree({ ...tree, taken: tree.taken.slice(0, -1) });
              }}
            >
              <Undo2 size={24} strokeWidth={2.5} />
            </ToyButton>
            <ToyButton
              round
              label="Start again"
              hideLabel
              disabled={tree.taken.length === 0}
              onClick={() => {
                play('whoosh', 0.6);
                setTree(createTree(live.current.target));
              }}
            >
              <RotateCcw size={24} strokeWidth={2.5} />
            </ToyButton>
          </div>
        </div>

        <p class="readout">
          {complete && tree.taken.length > 0 ? (
            <>
              <strong>{factorString(tree.target)}</strong>
              <span>{powerString(tree.target)}</span>
            </>
          ) : (
            <>
              <strong>{tree.target}</strong>
              <span>{complete ? 'is prime' : `now split ${treeRemainder(tree)}`}</span>
            </>
          )}
        </p>

        <Coach hideAfter={complete ? 60000 : 12000}>{factorHint(tree)}</Coach>
      </div>

      <div class="dock">
        {tabs}
        <div class="dock__row">
          <span class="dock__label">Take apart</span>
          <Chip label={String(tree.target)} onClick={() => setEditing(true)} />
          <div class="dock__spacer" />
          <ToyButton label="Split" disabled={complete} onClick={split} />
        </div>
      </div>

      {editing && (
        <NumberPad
          title="Which number?"
          value={tree.target}
          min={2}
          max={MAX_FACTOR_TARGET}
          onDone={(value) => {
            setEditing(false);
            setTree(createTree(value));
          }}
          onCancel={() => setEditing(false)}
        />
      )}
    </div>
  );
}

function line(ctx: CanvasRenderingContext2D, a: NodeBox, b: NodeBox): void {
  ctx.beginPath();
  ctx.moveTo(a.x, a.y);
  ctx.lineTo(b.x, b.y);
  ctx.stroke();
}

/** Shrink the type so six digits still fit inside a circle. */
function fitFont(text: string, radius: number): number {
  const base = radius * 0.85;
  return Math.max(11, Math.min(base, (radius * 1.7) / Math.max(1, text.length * 0.58)));
}
